import { useState, useEffect, useRef, useCallback } from "react";
import { Plus, Trash2, Pin, PinOff, Check, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductionNotepad() {
  const [notes, setNotes] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const saveTimer = useRef(null);

  const activeNote = notes.find(n => n.id === activeId);

  useEffect(() => {
    fetch(`${API}/notes`)
      .then(r => r.ok ? r.json() : { notes: [] })
      .then(data => {
        setNotes(data.notes || []);
        if (data.notes?.length > 0 && !activeId) setActiveId(data.notes[0].id);
      })
      .catch(() => {});
  }, []);

  const autoSave = useCallback((noteId, title, content) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await fetch(`${API}/notes/${noteId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ title, content }),
        });
        setLastSaved(new Date());
      } catch {}
      finally { setSaving(false); }
    }, 800);
  }, []);

  const updateNote = (field, value) => {
    setNotes(prev => prev.map(n => n.id === activeId ? { ...n, [field]: value } : n));
    const note = notes.find(n => n.id === activeId);
    if (note) {
      const updated = { ...note, [field]: value };
      autoSave(activeId, updated.title, updated.content);
    }
  };

  const createNote = async () => {
    try {
      const res = await fetch(`${API}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: "New Note", content: "" }),
      });
      if (!res.ok) return;
      const data = await res.json();
      setNotes(prev => [data, ...prev]);
      setActiveId(data.id);
    } catch {}
  };

  const deleteNote = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await fetch(`${API}/notes/${id}`, { method: "DELETE" });
      setNotes(prev => prev.filter(n => n.id !== id));
      if (activeId === id) setActiveId(notes.find(n => n.id !== id)?.id || null);
    } catch {}
  };

  const togglePin = async (id) => {
    const note = notes.find(n => n.id === id);
    if (!note) return;
    const pinned = !note.pinned;
    try {
      await fetch(`${API}/notes/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pinned }),
      });
      setNotes(prev => prev.map(n => n.id === id ? { ...n, pinned } : n));
    } catch {}
  };

  const sorted = [...notes].sort((a, b) => {
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-testid="production-notepad">
      {/* Sidebar */}
      <div className="lg:col-span-1">
        <button onClick={createNote}
          className="w-full mb-4 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded-lg flex items-center justify-center gap-2 transition-colors"
          data-testid="new-note-btn">
          <Plus size={16} /> New Note
        </button>
        <div className="space-y-1.5 max-h-[60vh] overflow-y-auto" data-testid="notes-list">
          {sorted.length === 0 && (
            <p className="text-center text-gray-600 text-sm py-8">No notes yet. Create one!</p>
          )}
          {sorted.map(note => (
            <button key={note.id} onClick={() => setActiveId(note.id)}
              className={`w-full text-left p-3 rounded-lg border transition-all group ${
                activeId === note.id
                  ? "bg-red-600/10 border-red-500/30 text-white"
                  : "border-white/5 hover:border-white/15 text-gray-400 hover:text-white"
              }`}
              data-testid={`note-item-${note.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">
                    {note.pinned && <Pin size={10} className="inline mr-1 text-amber-500" />}
                    {note.title || "Untitled"}
                  </p>
                  <p className="text-[10px] text-gray-600 mt-0.5 truncate">
                    {note.content?.substring(0, 50) || "Empty note"}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Editor */}
      <div className="lg:col-span-3">
        {activeNote ? (
          <div className="rounded-lg border border-white/10 bg-[#0a0a0a] overflow-hidden" data-testid="note-editor">
            {/* Title bar */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <input
                value={activeNote.title}
                onChange={e => updateNote("title", e.target.value)}
                className="flex-1 bg-transparent text-lg font-cinzel font-bold text-white focus:outline-none placeholder:text-gray-600"
                placeholder="Note title..."
                data-testid="note-title-input"
              />
              <div className="flex items-center gap-2 flex-shrink-0">
                {saving ? (
                  <span className="text-[10px] text-amber-500 flex items-center gap-1"><Clock size={10} /> Saving...</span>
                ) : lastSaved ? (
                  <span className="text-[10px] text-green-500 flex items-center gap-1"><Check size={10} /> Saved</span>
                ) : null}
                <button onClick={() => togglePin(activeNote.id)}
                  className={`p-1.5 rounded hover:bg-white/10 transition-colors ${activeNote.pinned ? "text-amber-500" : "text-gray-500"}`}
                  data-testid="pin-note-btn">
                  {activeNote.pinned ? <PinOff size={14} /> : <Pin size={14} />}
                </button>
                <button onClick={() => deleteNote(activeNote.id)}
                  className="p-1.5 rounded text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                  data-testid="delete-note-btn">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            {/* Content */}
            <textarea
              value={activeNote.content}
              onChange={e => updateNote("content", e.target.value)}
              className="w-full min-h-[50vh] p-5 bg-transparent text-sm text-gray-300 leading-relaxed resize-none focus:outline-none placeholder:text-gray-700"
              placeholder="Write your ideas, plot notes, character concepts, production plans..."
              data-testid="note-content-input"
            />
          </div>
        ) : (
          <div className="flex items-center justify-center h-64 rounded-lg border border-white/10 bg-[#0a0a0a]">
            <div className="text-center">
              <p className="text-gray-600 text-sm mb-3">Select a note or create a new one</p>
              <button onClick={createNote}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded-lg transition-colors"
                data-testid="create-first-note-btn">
                <Plus size={14} className="inline mr-1" /> New Note
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
